import { Injectable } from '@angular/core';
import {
  Promotion,
  PromotionRegulation,
  UpdatePromotionData,
  UpdatePromotionPrizes,
  UpdatePromotionUrl,
} from '../models/promotion';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { User } from '../models/user';
import { AuditorPromotion } from '../models/auditor-promotion';
import { UserService } from './user.service';
import { AccessLevel, FunctionalityName } from '../models/functionality';
import { map, of } from 'rxjs';
import { JointPromotion } from '../models/joint-promotion';
import {
  PreWinnerRowPayload,
  SavePreWinnersDataResponse,
} from '../models/save-pre-winners-data';

@Injectable({
  providedIn: 'root',
})
export class PromotionsService {
  constructor(
    private httpClient: HttpClient,
    private userService: UserService,
  ) { }

  listBRPromotions(queryParamsString?: string) {
    const userHasViewAllPermission = this.userService.userHasPermission(
      FunctionalityName.VIEW_ALL_PROMOS,
      AccessLevel.READ,
    );

    return this.httpClient.get<Promotion[]>(
      `${environment.auditBackendURL}${userHasViewAllPermission ? 'listAllPromotions' : 'listUserPromotions'
      }${queryParamsString ? `${queryParamsString}&` : '?'}countryList=BR,Brazil`,
    );
  }

  listLatamPromotions(queryParamsString?: string) {
    const userHasViewAllPermission = this.userService.userHasPermission(
      FunctionalityName.VIEW_ALL_LATAM_PROMOS,
      AccessLevel.READ,
    );

    return this.httpClient.get<Promotion[]>(
      `${environment.auditBackendURL}${userHasViewAllPermission ? 'listAllLatamPromotions' : 'listLatamUserPromotions'
      }${queryParamsString ? `?${queryParamsString}` : ''}`,
    )
  }

  listJointPromotions(queryParamsString?: string) {
    const userHasViewAllPermission = this.userService.userHasPermission(
      FunctionalityName.VIEW_ALL_PROMOS,
      AccessLevel.READ,
    );

    return this.httpClient.get<JointPromotion[]>(
      `${environment.auditBackendURL}${userHasViewAllPermission ? 'listAllJointPromotions' : 'listUserJointPromotions'
      }${queryParamsString ? `${queryParamsString}&` : '?'}countryList=BR,Brazil`,
    );
  }

  getPromotion(id: string) {
    return this.httpClient.get<Promotion>(
      `${environment.auditBackendURL}getPromotionById?id=${id}`,
    );
  }

  getJointPromotion(id: string) {
    return this.httpClient.get<JointPromotion>(
      `${environment.auditBackendURL}getJointPromotionById?id=${id}`,
    );
  }

  listPromotionAuditors(id: string) {
    return this.httpClient.get<User[]>(
      `${environment.auditBackendURL}listPromotionAuditors?promotionId=${id}`,
    );
  }

  assignPromotionToAuditor(data: AuditorPromotion) {
    return this.httpClient.post<AuditorPromotion>(
      `${environment.auditBackendURL}assignPromotionToAuditor`,
      data,
    );
  }

  deletePromotionFromAuditor({ promotionId, auditorId }: AuditorPromotion) {
    const params = new HttpParams()
      .set('promotionId', promotionId)
      .set('auditorId', auditorId);
    return this.httpClient.delete<AuditorPromotion>(
      `${environment.auditBackendURL}deletePromotionFromAuditor`,
      { params },
    );
  }

  updatePromotionUrl({ promotionId, promotionUrl }: UpdatePromotionUrl) {
    return this.httpClient.put<Promotion>(
      `${environment.auditBackendURL}updatePromotionUrl`,
      {
        promotionId,
        promotionUrl: promotionUrl ? encodeURIComponent(promotionUrl) : null,
      },
    );
  }

  updatePromotionPrizes(updatePromotionPrizes: UpdatePromotionPrizes) {
    return this.httpClient.put<Promotion>(
      `${environment.auditBackendURL}updatePromotionPrizes`,
      updatePromotionPrizes,
    );
  }

  updatePromotionData(updatePromotionData: UpdatePromotionData) {
    return this.httpClient.put<Promotion>(
      `${environment.auditBackendURL}updatePromotionData`,
      updatePromotionData,
    );
  }

  uploadPromotionRegulation(promotionId: string, fileUrl: string) {
    return this.httpClient.post<PromotionRegulation>(
      `${environment.auditBackendURL}updatePromotionRegulation`,
      {
        promotionId,
        fileUrl: encodeURIComponent(fileUrl),
      },
    );
  }

  downloadLatestPromotionRegulation(promotionId: string) {
    const params = new HttpParams().set('promoId', promotionId);

    return this.httpClient
      .get(`${environment.auditBackendURL}downloadLatestRegulation`, {
        params,
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        map((response) => {
          return {
            file: response.body,
            filename:
              response.headers.get('Regulation-Filename') ?? 'regulation',
          };
        }),
      );
  }

  /** Saves pre-winners directly. Frontend parses the file and sends rows (no Azure blob upload). */
  savePreWinnersData(promoId: string, preWinners: PreWinnerRowPayload[]) {
    return this.httpClient.post<SavePreWinnersDataResponse>(
      `${environment.auditBackendURL}savePreWinnersData`,
      { promotionId: promoId, preWinners },
    );
  }

  parseToBrazilianDatetime(dateInput: Date | string) {
    const threeHoursInMilliseconds = 3 * 60 * 60 * 1000;
    return new Date(new Date(dateInput).getTime() - threeHoursInMilliseconds);
  }

  userHasFairShare() {
    const userHasViewAllPermission = this.userService.userHasPermission(FunctionalityName.VIEW_ALL_PROMOS, AccessLevel.READ)
    if (userHasViewAllPermission) {
      return of(true)
    } else {
      return this.httpClient.get<JointPromotion[]>(
        `${environment.auditBackendURL}listUserJointPromotions?countryList=BR,Brazil`,
      ).pipe(
        map((promotions) => {
          if (promotions?.length) return true
          return false
        })
      );
    }
  }
}
